import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";
import { apiGet } from "@/lib/api";
import { hasWorkspacePermission } from "@/lib/access-control";
import { supabase } from "@/lib/supabase";
import { createProfileLifecycle } from "./profile-lifecycle";
import type { AccessUser, AuthMeResponse, Employee } from "@/types/api";

interface AuthContextValue {
  session: Session | null;
  access: AccessUser | null;
  employee: Employee | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  hasPermission: (permission?: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [access, setAccess] = useState<AccessUser | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);

  const profileLifecycle = useRef<ReturnType<
    typeof createProfileLifecycle
  > | null>(null);
  const loadProfile = useCallback(async () => {
    if (!profileLifecycle.current)
      throw new Error("Your session is still opening. Please try again.");
    await profileLifecycle.current.load();
  }, []);

  useEffect(() => {
    let active = true;
    let observedAuthEvent = false;
    const timers = new Set<number>();
    const lifecycle = createProfileLifecycle({
      fetchProfile: () => apiGet<AuthMeResponse>("/auth/me"),
      onProfile: (response) => {
        setAccess(response.access);
        setEmployee(response.user);
      },
      onReset: () => {
        // Shared query keys must never carry one account's records into another.
        queryClient.clear();
        setAccess(null);
        setEmployee(null);
      },
      onLoadingChange: setLoading,
    });
    profileLifecycle.current = lifecycle;

    function acceptSession(
      nextSession: Session | null,
      initial: boolean,
      refresh = false,
    ) {
      if (!active) return;
      setSession(nextSession);
      const changed = lifecycle.setIdentity(nextSession?.user.id ?? null);
      if (!nextSession || (!changed && !initial && !refresh)) return;
      // Supabase callbacks must finish before a request asks it for a token.
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        if (!lifecycle.isIdentity(nextSession.user.id)) return;
        void lifecycle.load().catch(() => {
          // Preserve the initial-load sign-out policy without signing out a newer account.
          if (initial && lifecycle.isIdentity(nextSession.user.id))
            void supabase.auth.signOut();
        });
      }, 0);
      timers.add(timer);
    }

    void supabase.auth
      .getSession()
      .then(({ data }) => {
        if (active && !observedAuthEvent) acceptSession(data.session, true);
      })
      .catch(() => {
        if (active && !observedAuthEvent) setLoading(false);
      });
    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        observedAuthEvent = true;
        acceptSession(
          nextSession,
          event === "INITIAL_SESSION",
          event === "SIGNED_IN",
        );
      },
    );
    return () => {
      active = false;
      lifecycle.dispose();
      for (const timer of timers) window.clearTimeout(timer);
      if (profileLifecycle.current === lifecycle)
        profileLifecycle.current = null;
      listener.subscription.unsubscribe();
    };
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      access,
      employee,
      loading,
      signIn: async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      },
      signOut: async () => {
        await supabase.auth.signOut();
        queryClient.clear();
      },
      refreshProfile: loadProfile,
      hasPermission: (permission) => hasWorkspacePermission(access, permission),
    }),
    [session, access, employee, loading, loadProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
